import React, { useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import Input from './ui/Input';
import { api } from '../services/authService';

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

export function offerLabel(hit) {
  const name = hit?.offer_name || '';
  const id = hit?.offer_id || '';
  if (id && name) return `${id}: ${name}`;
  return id || name || '';
}

/**
 * Typeahead over `/api/v2/offer_reports/typeahead`. Selecting a hit commits it;
 * clearing the input drops the filter.
 */
const OfferSearchTypeahead = ({
  onSelect,
  onClear,
  resetKey = '',
  selectedLabel = '',
  placeholder = 'Search by offer ID or name...',
  ariaLabel = 'Search offers by ID or name',
  disabled = false,
  organizationId = '',
}) => {
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const optionId = (index) => `${reactId}-option-${index}`;
  const [query, setQuery] = useState(selectedLabel || '');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setQuery(selectedLabel || '');
    setHits([]);
    setSearchError('');
    setOpen(false);
    setLoading(false);
    setActiveIndex(-1);
    clearTimeout(timerRef.current);
    requestIdRef.current += 1;
  }, [resetKey, selectedLabel]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-option-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const runSearch = (q) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const trimmed = q.trim();
      if (!trimmed) {
        requestIdRef.current += 1;
        setHits([]);
        setSearchError('');
        setOpen(false);
        setLoading(false);
        setActiveIndex(-1);
        return;
      }
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setActiveIndex(-1);
      try {
        const params = { q: trimmed, size: 50 };
        if (organizationId) params.organization_id = organizationId;
        const res = await api.get('/api/v2/offer_reports/typeahead', { params });
        if (requestId !== requestIdRef.current) return;
        const nextHits = Array.isArray(res.data?.hits) ? res.data.hits : [];
        setHits(nextHits);
        setSearchError(res.data?.error || '');
        setOpen(true);
        setActiveIndex(nextHits.length > 0 && !res.data?.error ? 0 : -1);
      } catch (e) {
        if (requestId !== requestIdRef.current) return;
        setHits([]);
        setSearchError(formatApiErrorDetail(e.response?.data?.detail) || 'Offer search failed');
        setOpen(true);
        setActiveIndex(-1);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 200);
  };

  const handlePick = (hit) => {
    onSelect(hit);
    setQuery(offerLabel(hit));
    setOpen(false);
    setHits([]);
    setActiveIndex(-1);
  };

  const clearInput = () => {
    setQuery('');
    if (typeof onClear === 'function') onClear();
    runSearch('');
  };

  const showResults = open && !loading && !searchError && hits.length > 0;
  const activeOptionId = activeIndex >= 0 ? optionId(activeIndex) : undefined;
  const showClear = Boolean(query);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
      }
      return;
    }

    if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (!open || loading || searchError || hits.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < hits.length - 1 ? prev + 1 : 0));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : hits.length - 1));
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const index = activeIndex >= 0 && activeIndex < hits.length ? activeIndex : 0;
    handlePick(hits[index]);
  };

  return (
    <div className="relative w-full" ref={wrapRef}>
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          autoComplete="off"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          disabled={disabled}
          className={showClear ? 'pr-10' : ''}
          onChange={(e) => {
            if (disabled) return;
            const next = e.target.value;
            setQuery(next);
            if (!next.trim() && typeof onClear === 'function') {
              onClear();
            }
            runSearch(next);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (disabled) return;
            if (hits.length || searchError) setOpen(true);
          }}
        />
        {showClear && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:text-gray-800"
            aria-label="Clear search"
            onClick={clearInput}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      {open && (
        <ul
          id={listboxId}
          className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white p-1 shadow-md"
          role="listbox"
          ref={listRef}
        >
          {loading && (
            <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>
          )}
          {!loading && searchError && (
            <li className="px-3 py-2 text-sm text-red-600">{searchError}</li>
          )}
          {!loading && !searchError && hits.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">No offers found</li>
          )}
          {showResults &&
            hits.map((hit, index) => (
              <li key={hit.offer_id} role="presentation">
                <button
                  type="button"
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  data-option-index={index}
                  className={`w-full truncate rounded-md px-3 py-2 text-left text-sm text-gray-800 ${
                    index === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handlePick(hit)}
                >
                  {offerLabel(hit) || '—'}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default OfferSearchTypeahead;
